import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const STATUS_COLOR = {
  in_progress: "var(--mv-accent)",
  pending: "var(--mv-accent)",
  confirmed: "var(--mv-secondary)",
  alternative_proposed: "#7c3aed",
  cancelled: "#dc2626",
  completed: "var(--mv-text-muted)",
};

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// Visible day range
const START_HOUR = 8;
const END_HOUR = 21;
const SLOT_MIN = 30; // grid resolution
const PX_PER_MIN = 1.2; // 30 min => 36 px
const DAY_HEIGHT = (END_HOUR - START_HOUR) * 60 * PX_PER_MIN;

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7; // 0=Mo
  x.setDate(x.getDate() - dow);
  return x;
}

function fmtIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseTime(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m || 0);
}

function initials(name) {
  if (!name) return "?";
  return name.split(/\s+/).filter(Boolean).map((p) => p[0].toUpperCase()).slice(0, 2).join("");
}

// Lay out overlapping bookings per day into columns (1..N) so they share width
function layoutDay(bookings) {
  // Sort by start time, then duration desc
  const items = bookings
    .map((b) => {
      const startM = parseTime(b.time);
      const dur = (b.service?.duration_minutes || b.duration_minutes || 30) + (b.addon_duration || 0);
      return { ...b, _start: startM, _end: startM + Math.max(15, dur) };
    })
    .sort((a, b) => a._start - b._start || b._end - a._end);

  // Greedy column assignment
  const columns = []; // each col = array of {start,end}
  items.forEach((it) => {
    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      const last = columns[c][columns[c].length - 1];
      if (last._end <= it._start) {
        columns[c].push(it);
        it._col = c;
        placed = true;
        break;
      }
    }
    if (!placed) {
      it._col = columns.length;
      columns.push([it]);
    }
  });

  // Calculate group size (max simultaneous overlap covering each item)
  items.forEach((a) => {
    let total = 1;
    items.forEach((b) => {
      if (a !== b && a._start < b._end && b._start < a._end) total = Math.max(total, (b._col ?? 0) + 1);
    });
    a._groupSize = Math.max(total, (a._col ?? 0) + 1);
  });
  return items;
}

export default function BookingsCalendar({ bookings, onPickDay, onPickBooking }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [weekStart, setWeekStart] = useState(startOfWeek(today));

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const byDate = useMemo(() => {
    const map = {};
    (bookings || []).forEach((b) => { if (b.date) (map[b.date] = map[b.date] || []).push(b); });
    Object.keys(map).forEach((k) => { map[k] = layoutDay(map[k]); });
    return map;
  }, [bookings]);

  const goPrev = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const goNext = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const goToday = () => setWeekStart(startOfWeek(today));

  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
  const weekTitle = `${weekStart.toLocaleDateString("de-DE", { day: "numeric", month: "short" })} – ${weekEnd.toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" })}`;

  const hourMarks = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) hourMarks.push(h);

  return (
    <div className="mv-card overflow-hidden" data-testid="bookings-calendar">
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--mv-border)" }}>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
          <h2 className="font-display text-xl">{weekTitle}</h2>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={goPrev} className="p-2 rounded-md hover:bg-[var(--mv-surface)]" aria-label="Vorherige Woche">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button data-testid="cal-today" onClick={goToday} className="mv-btn-ghost text-xs">Heute</button>
          <button data-testid="cal-next" onClick={goNext} className="p-2 rounded-md hover:bg-[var(--mv-surface)]" aria-label="Nächste Woche">
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Header row: 7 day labels */}
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", background: "var(--mv-surface)" }}>
        <div />
        {days.map((d) => {
          const isToday = d.getTime() === today.getTime();
          const iso = fmtIso(d);
          const count = (byDate[iso] || []).length;
          return (
            <button
              key={iso}
              type="button"
              data-testid={`cal-day-${iso}`}
              onClick={() => count && onPickDay?.(iso)}
              className="text-center px-2 py-2 border-l text-xs"
              style={{ borderColor: "var(--mv-border)", cursor: count ? "pointer" : "default" }}
            >
              <div className="uppercase tracking-wider" style={{ color: "var(--mv-text-muted)" }}>{WEEKDAYS[(d.getDay() + 6) % 7]}</div>
              <div className="inline-flex items-center justify-center w-7 h-7 rounded-full mt-0.5"
                style={{ background: isToday ? "var(--mv-primary)" : "transparent", color: isToday ? "#fff" : "inherit", fontWeight: 600 }}>
                {d.getDate()}
              </div>
              {count > 0 && (
                <div className="text-[10px] mt-0.5" style={{ color: "var(--mv-text-muted)" }}>{count} Termin{count !== 1 ? "e" : ""}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Body: time gutter + 7 day columns */}
      <div className="grid relative" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", borderTop: "1px solid var(--mv-border)" }}>
        {/* Hour gutter */}
        <div className="relative" style={{ height: DAY_HEIGHT }}>
          {hourMarks.map((h) => (
            <div key={h} className="absolute left-0 right-0 text-[10px] text-right pr-2"
              style={{ top: (h - START_HOUR) * 60 * PX_PER_MIN - 6, color: "var(--mv-text-muted)" }}>
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {days.map((d) => {
          const iso = fmtIso(d);
          const dayItems = byDate[iso] || [];
          const isToday = d.getTime() === today.getTime();
          return (
            <div key={iso} className="relative border-l"
              style={{ height: DAY_HEIGHT, borderColor: "var(--mv-border)", background: isToday ? "rgba(200,122,95,0.04)" : "transparent" }}>
              {/* Horizontal half-hour grid lines */}
              {hourMarks.map((h) => (
                <div key={h} className="absolute left-0 right-0"
                  style={{ top: (h - START_HOUR) * 60 * PX_PER_MIN, borderTop: "1px solid var(--mv-border)", opacity: 0.6 }} />
              ))}
              {hourMarks.slice(0, -1).map((h) => (
                <div key={`${h}-half`} className="absolute left-0 right-0"
                  style={{ top: ((h - START_HOUR) * 60 + 30) * PX_PER_MIN, borderTop: "1px dashed var(--mv-border)", opacity: 0.4 }} />
              ))}

              {dayItems.map((b) => {
                const top = Math.max(0, (b._start - START_HOUR * 60)) * PX_PER_MIN;
                const height = Math.max(20, (b._end - b._start) * PX_PER_MIN - 2);
                const widthPct = 100 / (b._groupSize || 1);
                const leftPct = (b._col || 0) * widthPct;
                const color = STATUS_COLOR[b.status] || STATUS_COLOR.in_progress;
                const dur = b._end - b._start;
                const tall = height > 36;
                return (
                  <button
                    type="button"
                    key={b.id}
                    onClick={() => onPickBooking ? onPickBooking(b) : onPickDay?.(iso)}
                    data-testid={`cal-booking-${b.id}`}
                    className="absolute rounded-md px-1.5 py-1 text-left overflow-hidden border-l-2 transition-shadow hover:shadow-md"
                    style={{
                      top,
                      height,
                      left: `calc(${leftPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      background: `${color}1F`,
                      borderColor: color,
                      color: color,
                      textDecoration: b.status === "cancelled" ? "line-through" : "none",
                    }}
                    title={`${b.time} (${dur} Min) · ${b.customer_name} · ${b.service?.name || b.service_name || ""}${b.staff?.name ? " · " + b.staff.name : ""}`}
                  >
                    <div className="flex items-center justify-between gap-1 text-[10px] font-semibold">
                      <span>{b.time}</span>
                      {b.staff?.name && (
                        <span className="px-1 rounded-full text-[9px]" style={{ background: color, color: "#fff" }}>
                          {initials(b.staff.name)}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] truncate" style={{ color: "var(--mv-text)" }}>{b.customer_name}</div>
                    {tall && (
                      <div className="text-[10px] truncate opacity-75" style={{ color: "var(--mv-text)" }}>
                        {b.service?.name || b.service_name}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
